import { getService } from "@/lib/services-data";
import { getServiceConversion } from "@/lib/service-conversion";

export interface ServiceFaq {
  q: string;
  a: string;
}

/**
 * Hand-written FAQs per service. Answers must stay factually accurate:
 * we provide legal information and legal advice on non-reserved matters,
 * legal opinions, paralegal, drafting and compliance support — no reserved
 * work and no court/CCMA representation.
 */
const FAQS: Record<string, ServiceFaq[]> = {
  "labour-relations": [
    {
      q: "Can you represent me at a disciplinary hearing?",
      a: "We prepare you fully — charge-sheet analysis, procedural checks, evidence bundles, witness statements and a hearing script — but we do not appear as your representative. Where representation is required we refer you to an admitted attorney or a registered union official.",
    },
    {
      q: "How quickly can you help with a dismissal?",
      a: "Labour timelines are strict: an unfair dismissal must reach the CCMA within 30 days. Send us your documents and we normally return an assessment and referral pack within 2–3 working days.",
    },
    {
      q: "Do you help employers as well as employees?",
      a: "Yes. We draft disciplinary codes, contracts of employment, warnings and retrenchment (section 189) processes for employers, and we assist employees with grievances, appeals and referrals.",
    },
    {
      q: "What documents should I send you first?",
      a: "Your employment contract, payslips, the disciplinary notice or dismissal letter, any warnings, and any correspondence with your employer. That is usually enough for a first assessment.",
    },
  ],
  "ccma-support": [
    {
      q: "Do you appear at the CCMA on my behalf?",
      a: "No. We prepare the matter — LRA 7.11 referral, condonation where needed, case strategy, bundles, witness statements and coaching — so you can present it confidently yourself. We do not appear at conciliation or arbitration.",
    },
    {
      q: "I am past the 30-day deadline. Is it too late?",
      a: "Not necessarily. You can apply for condonation and explain the delay. We prepare the condonation affidavit setting out the degree of lateness, the reasons, your prospects of success and the prejudice to each party.",
    },
    {
      q: "What does the CCMA charge?",
      a: "The CCMA itself does not charge a filing fee for referrals. Our fee covers preparation of the referral, the bundle and your coaching session, and is quoted in writing up front.",
    },
    {
      q: "How long does a CCMA matter take?",
      a: "Conciliation is usually set down within 30 days of referral, with arbitration following if the matter is not settled. Timelines vary by region and commissioner availability.",
    },
  ],
  "legal-drafting": [
    {
      q: "Are your contracts compliant with South African law?",
      a: "Yes. Every document is drafted against current South African legislation — the BCEA, LRA, CPA, POPIA, the Companies Act and relevant case law — and is tailored to your facts rather than a generic template.",
    },
    {
      q: "How many revisions are included?",
      a: "Two rounds of revisions are included in the quoted fee. Further changes are quoted separately before we start.",
    },
    {
      q: "Can you review a contract someone else drafted?",
      a: "Yes. We provide a clause-by-clause review with a risk rating, plain-English commentary and suggested replacement wording.",
    },
    {
      q: "Do you certify or notarise documents?",
      a: "No. Notarial work is reserved for admitted notaries. We prepare the document and refer you to a notary or commissioner of oaths where signature formalities are required.",
    },
  ],
  compliance: [
    {
      q: "What does a POPIA compliance pack include?",
      a: "A gap assessment, privacy policy, PAIA manual pointers, consent and data-subject request forms, an operator agreement template, a data-breach procedure and a short staff training session.",
    },
    {
      q: "Do you register the Information Officer with the Regulator?",
      a: "We prepare the registration and guide you through the Information Regulator's portal. The registration is submitted in your organisation's name.",
    },
    {
      q: "How long does a compliance audit take?",
      a: "For a small business, typically 5–10 working days from receiving your documents, depending on how many policies and processes need review.",
    },
    {
      q: "Can you help with B-BBEE and BCEA compliance too?",
      a: "Yes. We cover employment-law compliance (BCEA, EEA), B-BBEE readiness, and internal policy frameworks alongside POPIA.",
    },
  ],
  "debt-assistance": [
    {
      q: "Is this the same as debt review?",
      a: "No. Debt review is a statutory process run by a registered debt counsellor. We assist with rights under the National Credit Act, prescription, reckless-credit arguments, negotiation with creditors and written settlement arrangements, and we refer you to a registered debt counsellor when review is the right route.",
    },
    {
      q: "Can you stop a garnishee or emoluments attachment order?",
      a: "We review whether the order was lawfully obtained and prepare the challenge or rescission papers. Court appearances are referred to an admitted attorney.",
    },
    {
      q: "What is prescription and does it help me?",
      a: "Most ordinary debts prescribe after three years of no acknowledgement or payment. We check the dates and, where applicable, draft the letter raising prescription.",
    },
    {
      q: "Will you speak to my creditors?",
      a: "Yes — we draft and send negotiation and settlement correspondence on your instructions and record the agreed arrangement in writing.",
    },
  ],
  "court-online": [
    {
      q: "What is Court Online and CaseLines?",
      a: "They are the South African judiciary's electronic case-management and digital-bundle platforms used in the High Courts. Court Online handles issuing and filing; CaseLines hosts the evidence bundle used in court.",
    },
    {
      q: "Can you file on my behalf?",
      a: "We provide setup, uploading, indexing, pagination and hyperlinking support. Filing in a litigious matter is done by the attorney of record or the litigant themselves.",
    },
    {
      q: "Do you train firms and in-house teams?",
      a: "Yes. We run practical, hands-on sessions for small firms, paralegals and in-house teams, in person or over Teams/Zoom, with a written quick-reference guide afterwards.",
    },
    {
      q: "My bundle keeps getting rejected. Can you fix it?",
      a: "Yes — we re-index, re-paginate and correct the bundle structure so it meets the practice directives.",
    },
  ],
  "case-management": [
    {
      q: "What does case management actually cover?",
      a: "Intake, a structured matter file, a chronology, deadline diarising, document collection, correspondence tracking and status reporting so nothing is missed.",
    },
    {
      q: "Do you take instructions as an attorney?",
      a: "No. We are not an attorney firm. We manage the file and prepare the work; reserved steps are referred to an admitted attorney who remains the practitioner of record.",
    },
    {
      q: "How do you keep my information confidential?",
      a: "All files are handled under POPIA, stored on access-controlled systems, and shared only with people you authorise in writing.",
    },
    {
      q: "Can you take over a matter that has gone stale?",
      a: "Yes. We audit the existing file, rebuild the chronology, identify missed deadlines and set out a recovery plan.",
    },
  ],
  "public-speaking": [
    {
      q: "What topics do you present on?",
      a: "Labour rights and CCMA procedure, POPIA and data protection, consumer and credit rights, contract basics for small business, and legal study and writing skills.",
    },
    {
      q: "Do you travel for engagements?",
      a: "Yes — nationally for in-person sessions, and we present online over Teams or Zoom anywhere in South Africa.",
    },
    {
      q: "Are materials provided to attendees?",
      a: "Yes. Every session includes a slide pack, a plain-English handout and a short knowledge check where useful.",
    },
    {
      q: "Can the session be tailored to my organisation?",
      a: "Yes. We brief with you first and build the content around your sector, workforce and real scenarios.",
    },
  ],
  "drafting-paralegal": [
    {
      q: "What is the difference between this and using an attorney?",
      a: "We do the research, drafting and file preparation at a lower cost. Work reserved by the Legal Practice Act 28 of 2014 — court appearances, conveyancing and notarial work — is referred to an admitted attorney.",
    },
    {
      q: "Can you draft affidavits and letters of demand?",
      a: "Yes. We draft affidavits, letters of demand, agreements, notices and internal policies, and prepare them for signature and commissioning.",
    },
    {
      q: "Do you provide legal advice?",
      a: "We provide legal information and legal advice on non-reserved matters, and we prepare written legal opinions. We do not undertake reserved work or represent clients in court.",
    },
    {
      q: "How is the work delivered?",
      a: "As a signed-off PDF plus an editable Word file, with a short covering note explaining the key clauses and next steps.",
    },
  ],
  "company-secretarial": [
    {
      q: "Which CIPC filings do you handle?",
      a: "Annual returns, director and auditor changes, registered-address changes, MOI amendments, name reservations and beneficial-ownership filings.",
    },
    {
      q: "What happens if my annual returns are overdue?",
      a: "The company risks deregistration. We calculate the outstanding returns and penalties, bring the filings up to date and, where needed, prepare a re-instatement application.",
    },
    {
      q: "Do you maintain statutory registers?",
      a: "Yes. We build and maintain the share register, director register, beneficial-ownership register and minute book, and keep them filing-ready.",
    },
    {
      q: "Can you act as company secretary for a small company?",
      a: "We provide company secretarial support services and prepare board and shareholder documentation. Formal appointment as company secretary is arranged only where the Companies Act permits it for your entity type.",
    },
  ],
};

const GENERIC_FALLBACK = (slug: string): ServiceFaq[] => {
  const service = getService(slug);
  const conv = getServiceConversion(slug);
  const title = service?.title ?? "this service";
  return [
    {
      q: `What is included in ${title.toLowerCase()}?`,
      a: conv.deliverables.length
        ? `You receive ${conv.deliverables.join(", ")}.`
        : service?.details ?? "A tailored scope agreed in writing before work begins.",
    },
    {
      q: `How long does ${title.toLowerCase()} take?`,
      a: conv.turnaround,
    },
    {
      q: "What does it cost?",
      a: `${conv.pricingHint} You always receive a fixed written quote before any work begins.`,
    },
    {
      q: "Do you provide legal advice or court representation?",
      a: "We provide legal information and legal advice on non-reserved matters and prepare legal opinions. We do not undertake work reserved by the Legal Practice Act 28 of 2014 and we do not represent clients in court.",
    },
  ];
};

export function getServiceFaqs(slug: string): ServiceFaq[] {
  return FAQS[slug] ?? GENERIC_FALLBACK(slug);
}

export function buildFaqPageJsonLd(slug: string, canonical: string) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${canonical}#faq`,
    mainEntity: getServiceFaqs(slug).map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}
